import { useQueryClient } from "@tanstack/react-query";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { useToast } from "../../../../mantine/notifications/useToast";
import { SubMeteringSystem } from "../../../../types/api.types";
import type { Meter, Site } from "../../../../utils/backend-types";
import type { SelectedMeter } from "../../EditConfigurationModal/EditConfigurationModal";
import { updateMeterParametersCache } from "../../hooks/useMeterParameters";
import type { MeterConfigurationData } from "../../ThirdPartySystems.types";
import { getParameterName } from "../../utils/getParameterName";
import { updateEntitiesMeterData } from "../../utils/updateEntitiesMeterData";
import { getByteMeeColumns } from "./columns/getByteMeeColumns";
import { getGreenByteColumns } from "./columns/getGreenByteColumns";
import { getMetiundoColumns } from "./columns/getMetiundoColumns";
import { getOptiNodeMeterColumn } from "./columns/getOptiNodeMeterColumn";
import { getQivaloColumns } from "./columns/getQivaloColumns";
import { getRotorsoftColumns } from "./columns/getRotorsoftColumns";
import { getSiemensColumns } from "./columns/getSiemensColumns";
import { getWattlineColumns } from "./columns/getWattlineColumns";
import { getWISColumns } from "./columns/getWISColumns";
import { getWonderColumns } from "./columns/getWonderColumns";

interface MeterConfigurationTableProps {
  configurationId?: number;
  systemName: SubMeteringSystem;
  sites: Site[];
  meterParams?: MeterConfigurationData[];
  isMeterParamsLoading: boolean;
  isSuggestionsLoading: boolean;
  metersBySiteId?: Meter[];
  isMetersQueryLoading: boolean;
  selectedMeters: Array<SelectedMeter>;
  onMetersSave: (singleEntityId?: number) => Promise<{ success: boolean }>;
  setSelectedMeters: Dispatch<SetStateAction<SelectedMeter[]>>;
}

function MeterConfigurationTable({
  systemName,
  configurationId,
  sites,
  meterParams,
  isMeterParamsLoading,
  isSuggestionsLoading,
  metersBySiteId,
  isMetersQueryLoading,
  selectedMeters,
  onMetersSave,
  setSelectedMeters
}: MeterConfigurationTableProps) {
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const handleRemoveMeter = useCallback(
    async (entityId: number) => {
      try {
        await updateEntitiesMeterData(configurationId as number, [
          { entityId, meterId: null }
        ]);

        updateMeterParametersCache(
          entityId,
          { meter: null },
          queryClient,
          configurationId
        );
      } catch (error) {
        showErrorToast(error);
      }
    },
    [configurationId, queryClient, showErrorToast]
  );

  const handleSelectMeter = useCallback(
    (entityId: number, meterId: number) => {
      const isMeterSelected = !!selectedMeters.find(
        (meter) => meter.subMeteringSystemEntityId === entityId
      );

      // when meter is deselected
      if (meterId === 0) {
        setSelectedMeters((prevState) =>
          prevState.filter(
            (meter) => meter.subMeteringSystemEntityId !== entityId
          )
        );
        return;
      }

      const meterName =
        metersBySiteId?.find((meter) => meter.id === meterId)?.name || "";

      const parameter = meterParams?.find((param) => param.id === entityId);

      const parameterName = getParameterName(systemName, parameter);

      if (isMeterSelected) {
        setSelectedMeters((prevState) =>
          prevState.map((selectedMeter) =>
            selectedMeter.subMeteringSystemEntityId === entityId
              ? { ...selectedMeter, meterId, meterName }
              : selectedMeter
          )
        );
        return;
      }

      setSelectedMeters((prevState) => [
        ...prevState,
        {
          subMeteringSystemEntityId: entityId,
          meterId,
          meterName,
          parameterName
        }
      ]);
    },
    [metersBySiteId, meterParams, selectedMeters, systemName, setSelectedMeters]
  );

  const cols: Array<MRT_ColumnDef<MeterConfigurationData>> = useMemo(() => {
    const defaultColumn = getOptiNodeMeterColumn(
      metersBySiteId || [],
      selectedMeters,
      sites,
      handleSelectMeter,
      handleRemoveMeter,
      onMetersSave
    );
    switch (systemName) {
      case SubMeteringSystem.WeoBytemee:
        return [...getByteMeeColumns(), defaultColumn];
      case SubMeteringSystem.BreezeGreenbyte:
        return [...getGreenByteColumns(), defaultColumn];
      case SubMeteringSystem.Qivalo:
        return [...getQivaloColumns(), defaultColumn];
      case SubMeteringSystem.RotorsoftDrehpunkt:
        return [...getRotorsoftColumns(), defaultColumn];
      case SubMeteringSystem.NavigatorSiemens:
        return [...getSiemensColumns(), defaultColumn];
      case SubMeteringSystem.Wattline:
        return [...getWattlineColumns(), defaultColumn];
      case SubMeteringSystem.Metiundo:
        return [...getMetiundoColumns(), defaultColumn];
      case SubMeteringSystem.WisSoftenergy:
        return [...getWISColumns(), defaultColumn];
      case SubMeteringSystem.WonderWindguard:
        return [...getWonderColumns(), defaultColumn];
      default:
        return [];
    }
  }, [
    systemName,
    metersBySiteId,
    selectedMeters,
    sites,
    handleRemoveMeter,
    handleSelectMeter,
    onMetersSave
  ]);

  const table = useMantineReactTable<MeterConfigurationData>({
    ...getDefaultMRTOptions({
      initialState: { showColumnFilters: true },
      hideBorder: true
    }),
    state: {
      isLoading:
        isMeterParamsLoading || isSuggestionsLoading || isMetersQueryLoading
    },
    enableTopToolbar: false,
    columnFilterDisplayMode: "popover",
    columns: cols,
    data: meterParams || []
  });

  return <MantineReactTable table={table} />;
}

export { MeterConfigurationTable, MeterConfigurationTableProps };
